import { Link, Table, TableBody, TableCell, TableContainer, TableHead } from '@material-ui/core';
import { TablePagination, TableRow, Tooltip, Typography } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import { makeStyles } from '@material-ui/core/styles';
import { ChangeEvent, FunctionComponent, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import FilterRow, { useTableFiltering } from './filterRow';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%'
    },
    table: {
        '& thead $headerLabelsRow th': {
            top: 0,
            zIndex: 3
        },
        '& thead $filterRow th': {
            zIndex: 2
        }
    },
    headerLabelsRow: {},
    container: {
        height: '100%',
        overflowX: 'initial'
    },
    inlineValueContainer: {
        display: 'flex',
        placeItems: 'center'
    },
    paging: {
        color: theme.palette.text.secondary
    },
    linebreakValueContainer: {
        whiteSpace: 'pre-line'
    },
    filterRow: {
        backgroundColor: theme.palette.grey[100]
    },
    filterCell: {
        padding: theme.spacing(1)
    }
}));

export enum FilterType {
    TEXT = 'text',
    SELECT = 'select'
}

export type DataTableColumn = {
    field: string,
    label: string,
    minWidth?: number,
    format?: (value: any) => string;
    viewType?: string;
    tooltipField?: string;
    tooltipHeader?: string;
    linkHash?: (value: string) => string;
    suffixComponent?: (value: any) => React.ReactElement;
    submittableComponent?: (row: any, value: string) => React.ReactElement;
    filterable?: boolean;
    filterType?: FilterType;
};

type Props = {
    columns: DataTableColumn[];
    rows?: {[key: string]: any}[];
    totalRows?: number;
    currentPage: number;
    currentPageChanged: (newPage: number) => void;
    rowsPerPage: number;
    rowsPerPageChanged: (newRowsPerPage: number) => void;
    onLinkClicked?: (newHash: string) => void;
    filters?: {[key: string]: string};
    showFilters?: boolean;
    allRows?: {[key: string]: any}[];
};

export const getRowColumnValue = (row: {[key: string]: any}, columnId: string): any => {
    const nextSeparatorIndex = columnId.indexOf('|');
    if (nextSeparatorIndex > -1) {
        return getRowColumnValue(row[columnId.slice(0, nextSeparatorIndex)], columnId.slice(nextSeparatorIndex + 1));
    } else {
        return row[columnId];
    }
};

const DataTable: FunctionComponent<Props> = ({
    columns, rows, totalRows, currentPage, currentPageChanged, rowsPerPage, rowsPerPageChanged, filters: filtersProp, showFilters = false, allRows
}: Props) => {
    const classes = useStyles();
    const headerLabelsRowRef = useRef<HTMLTableRowElement>(null);
    const [ filterStickyTopPx, setFilterStickyTopPx ] = useState(56);
    const [ internalFilters, setInternalFilters ] = useState<{[key: string]: string}>({});

    useLayoutEffect(() => {
        if (!showFilters) {
            return;
        }
        const row = headerLabelsRowRef.current;
        if (!row) {
            return;
        }
        const sync = () => {
            setFilterStickyTopPx(Math.ceil(row.getBoundingClientRect().height));
        };
        sync();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
        ro?.observe(row);
        return () => ro?.disconnect();
    }, [ showFilters, columns ]);
    
    // Use prop filters if provided (controlled), otherwise use internal state (uncontrolled)
    const filters = filtersProp !== undefined ? filtersProp : internalFilters;

    const handleChangePage = useCallback(
        (_, newPage: number) => {
            currentPageChanged(newPage);
        },
        [ currentPageChanged ]
    );

    const handleChangeRowsPerPage = useCallback(
        (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            rowsPerPageChanged(+event.target.value);
            currentPageChanged(0);
        },
        [ currentPageChanged, rowsPerPageChanged ]
    );

    const handleFilterChange = useCallback(
        (field: string, value: string) => {
            if (filtersProp !== undefined) {
                // Controlled mode - should not happen when showFilters is true, but handle gracefully
                return;
            }
            
            // Uncontrolled mode - manage filters internally
            setInternalFilters((prevFilters) => {
                const newFilters = { ...prevFilters };
                if (value.trim() === '') {
                    delete newFilters[field];
                } else {
                    newFilters[field] = value;
                }
                return newFilters;
            });
            
            // Reset to first page when filter changes
            currentPageChanged(0);
        },
        [ filtersProp, currentPageChanged ]
    );

    const { filteredRows, displayTotalRows } = useTableFiltering({
        columns,
        allRows,
        rows,
        filters,
        totalRows,
        showFilters
    });

    const hasActiveFilters = filters && Object.values(filters).some((value) => value.trim() !== '');

    const displayRows = useMemo(() => {
        if (allRows && hasActiveFilters) {
            const start = currentPage * rowsPerPage;
            return filteredRows.slice(start, start + rowsPerPage);
        }
        return filteredRows;
    }, [ allRows, hasActiveFilters, filteredRows, currentPage, rowsPerPage ]);

    const getCellContent = (row: {[key: string]: any}, column: DataTableColumn) => {
        const value: string = getRowColumnValue(row, column.field);
        const formattedValue: string = column.format ? column.format(value) : value;

        let valueComponent = (
            <Typography color="textSecondary">
                {formattedValue}
            </Typography>
        );

        // wrap component with link if needed
        if (column.linkHash) {
            valueComponent = (
                <Link href={`#${column.linkHash(value)}`}>
                    {valueComponent}
                </Link>
            );
        }

        // wrap component with tooltip if needed
        if (column.tooltipField && getRowColumnValue(row, column.tooltipField) ) {
            valueComponent = (
                <Tooltip title={
                    <span className={classes.linebreakValueContainer}>
                        {getRowColumnValue(row, column.tooltipField)}
                    </span>
                }>
                    {valueComponent}
                </Tooltip>
            );
        }

        // add suffix component if needed
        if (column.suffixComponent) {
            valueComponent = (
                <span className={classes.inlineValueContainer}>
                    {valueComponent}
                    {column.suffixComponent(value)}
                </span>
            );
        }

        // add empty component if needed
        if (column.submittableComponent) {
            if (column.tooltipField && getRowColumnValue(row, column.tooltipField) ) {
                valueComponent = (
                    <Tooltip title={
                        <span className={classes.linebreakValueContainer}>
                            {getRowColumnValue(row, column.tooltipField)}
                        </span>
                    }>
                        <span className={classes.inlineValueContainer}>
                            {column.submittableComponent(row, formattedValue)}
                        </span>
                    </Tooltip>
                );
            } else {
                valueComponent = (
                    <span className={classes.inlineValueContainer}>
                        {column.submittableComponent(row, formattedValue)}
                    </span>
                );
            }
        }

        if (column.viewType === 'gauge') {
            valueComponent = <div>
                <LinearProgress variant="determinate" value={Number(value)} />
                <Typography variant="body2" color="primary">{`${formattedValue}%`}</Typography>
            </div>;
        }

        // return TableCell component with value
        return (
            <TableCell key={column.field}>
                {valueComponent}
            </TableCell>
        );
    };

    return (
        <>
            <TableContainer className={classes.container}>
                <Table stickyHeader className={classes.table}>
                    <TableHead>
                        <TableRow ref={headerLabelsRowRef} className={classes.headerLabelsRow}>
                            {columns.map((column) => (
                                <Tooltip key={column.field} title={column.tooltipHeader ?? ''}>
                                    <TableCell
                                        key={column.field}
                                        style={{ minWidth: column.minWidth }}
                                    >
                                        {column.label}
                                    </TableCell>
                                </Tooltip>
                                
                            ))}
                        </TableRow>
                        {showFilters && (
                            <FilterRow
                                columns={columns}
                                filters={filters}
                                onFilterChange={handleFilterChange}
                                allRows={allRows}
                                rows={rows}
                                showFilters={showFilters}
                                stickyTopPx={filterStickyTopPx}
                                classes={{
                                    filterRow: classes.filterRow,
                                    filterCell: classes.filterCell
                                }}
                            />
                        )}
                    </TableHead>
                    <TableBody>
                        {displayRows.map((row, rowIndex) => {
                            return (
                                <TableRow
                                    hover
                                    role="checkbox"
                                    tabIndex={-1}
                                    key={`work-row-${rowIndex}`}
                                >
                                    {columns.map((column) => getCellContent(row, column))}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                className={classes.paging}
                rowsPerPageOptions={[ 25, 50, 100 ]}
                component="div"
                count={displayTotalRows}
                rowsPerPage={rowsPerPage}
                page={currentPage}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
            />
        </>
    );
};

export default DataTable;
